import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import {
  Card,
  Tree,
  Button,
  Space,
  Tag,
  Typography,
  Progress,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Switch,
  message,
  Popconfirm,
  Tooltip,
  Row,
  Col,
  Statistic,
  Alert,
  Divider,
  Checkbox,
  Spin
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SaveOutlined,
  CheckOutlined,
  FileTextOutlined,
  UserOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  ArrowLeftOutlined,
  CloseOutlined,
  BulbOutlined,
} from '@ant-design/icons'
import './PersonnelAssignment.css'
import { useDispatch, useSelector } from 'react-redux'
import type { RootState } from '../../store'
import {
  fetchAssignmentData,
  saveAssignmentData,
  validateAssignmentData,
  updateMilestoneAssignment,
  addSubtask,
  removeSubtask,
  updateSubtask,
  setValidationErrors,
  clearValidationErrors,
  resetAssignmentState
} from '../../store/slices/assignmentsSlice'
import { fetchMilestones } from '../../store/slices/milestonesSlice'
import { fetchPlanDetail } from '../../store/slices/planogramPlansSlice'
import { assignmentService } from '../../services/assignmentService'
import operatorGroupService from '../../services/operatorGroupService'
import PlanogramSubtaskService from '../../services/planogramSubtaskService'
import type { DataNode } from 'antd/es/tree'
import type { SubtaskConfiguration, ValidationError } from '../../types'
import { localizeValidationError } from '../../utils/i18nUtils'
import dayjs from 'dayjs'

const { Title, Text } = Typography
const { Option } = Select
const { TextArea } = Input

interface SubtaskFormData {
  taskName: string
  assignee: string
  plannedCompletionTime: dayjs.Dayjs
  requiresFiles: boolean
  description?: string
}

const PersonnelAssignment: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  
  // Redux state
  const { assignmentData, validationErrors, isDirty, saving, error } = useSelector((state: RootState) => state.assignments)
  const { milestones, loading: milestonesLoading } = useSelector((state: RootState) => state.milestones)
  const { currentPlan, loading: planLoading } = useSelector((state: RootState) => state.planogramPlans)
  
  // Local state
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([])
  const [selectedMilestone, setSelectedMilestone] = useState<string>('')
  const [subtaskModalVisible, setSubtaskModalVisible] = useState(false)
  const [editingSubtask, setEditingSubtask] = useState<{ milestoneId: string; index: number } | null>(null)
  const [availablePersonnel, setAvailablePersonnel] = useState<any[]>([])
  const [directors, setDirectors] = useState<any[]>([]) // 新增：存储负责人列表
  const [selectedDirector, setSelectedDirector] = useState<string>('') // 新增：存储选中的负责人
  const [submitting, setSubmitting] = useState(false)
  const [form] = Form.useForm<SubtaskFormData>()

  // Handle back navigation
  const handleBack = () => {
    // 检查来源页面，根据来源返回到对应的页面
    const from = location.state?.from;
    
    if (from === '/detail') {
      // 如果来自计划详情页面，返回到详情页面
      navigate(`/detail/${id}`, { replace: true });
    } else {
      // 默认返回到计划列表页面
      navigate('/plans', { replace: true });
    }
  }

  // Get personnel workload information
  const getPersonnelWorkload = (assigneeId: string) => {
    let currentWorkload = 0
    assignmentData?.assignments?.forEach(assignment => {
      assignment.subtasks?.forEach(subtask => {
        if (subtask.assignee === assigneeId) {
          currentWorkload++
        }
      })
    })
    return currentWorkload
  }

  // Check for time conflicts (DEPRECATED - no longer used)
  // const checkTimeConflicts = (assigneeId: string, plannedTime: dayjs.Dayjs, excludeSubtask?: { milestoneId: string; index: number }) => {
  //   const conflicts: string[] = []
  //   
  //   assignmentData?.assignments.forEach(assignment => {
  //     assignment.subtasks.forEach((subtask, index) => {
  //       if (subtask.assignee === assigneeId && 
  //           !(excludeSubtask?.milestoneId === assignment.milestoneId && excludeSubtask?.index === index)) {
  //         const subtaskTime = dayjs(subtask.plannedCompletionTime)
  //         const timeDiff = Math.abs(plannedTime.diff(subtaskTime, 'hour'))
  //         
  //         if (timeDiff < 4) { // Less than 4 hours apart
  //           const milestoneName = milestones.find(m => m.milestoneId === assignment.milestoneId)?.milestoneName
  //           conflicts.push(`与"${milestoneName}"中的"${subtask.taskName}"时间冲突`)
  //         }
  //       }
  //     })
  //   })
  //   
  //   return conflicts
  // }

  useEffect(() => {
    if (id) {
      // Fetch plan details
      dispatch(fetchPlanDetail(id) as any)
      // Fetch milestones (using id parameter)
      dispatch(fetchMilestones(id) as any)
      // Fetch assignment data (using id parameter)
      dispatch(fetchAssignmentData(id) as any)
    }

    return () => {
      dispatch(resetAssignmentState())
    }
  }, [id, dispatch])

  // Load personnel data on component mount
  useEffect(() => {

  }, [])

  // Initialize expanded keys when milestones are loaded
  useEffect(() => {
    if (milestones.length > 0) {
      setExpandedKeys(milestones.map(m => m.milestoneId))
    }
  }, [milestones])

  // Calculate assignment progress
  const calculateAssignmentProgress = (): { completed: number; total: number; percentage: number } => {
    if (!milestones || milestones.length === 0) {
      return { completed: 0, total: 0, percentage: 0 }
    }
    
    // If plan is in progress, consider all milestones as completed
    if (currentPlan?.status === 'InProgress') {
      return {
        completed: milestones.length,
        total: milestones.length,
        percentage: 100
      }
    }
    
    let completed = 0
    // 排除调用算法的和生成实操的
    let total = milestones.length - 2
    
    assignmentData?.assignments?.forEach(assignment => {
      if (assignment.subtasks && assignment.subtasks.length > 0) {
        completed++
      }
    })
    
    return {
      completed,
      total,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0
    }
  }

  // Generate tree data for milestone display
  const generateTreeData = (): DataNode[] => {
    return milestones.map(milestone => {
      const assignment = assignmentData?.assignments.find(a => a.milestoneId === milestone.milestoneId)
      const hasOwner = true // 简化逻辑，假设总是有负责人
      const subtaskCount = assignment?.subtasks?.length || 0
      
      return {
        key: milestone.milestoneId,
        title: (
          <div className="milestone-tree-node">
            <div className="milestone-header">
              <Space>
                <Tag className={hasOwner ? 'milestone-tag' : 'milestone-tag pending'}>
                  M{milestone.milestoneSequence.toString().padStart(3, '0')}
                </Tag>
                <Text className="milestone-name">{milestone.milestoneName}</Text>
                <Text className="milestone-department">({milestone.responsibleDepartment})</Text>
                {subtaskCount > 0 && (
                  <Tag className="subtask-count-tag">{subtaskCount}{t('planManagement.assignment.subtaskCount')}</Tag>
                )}
              </Space>
            </div>
            <div className="milestone-assignment">
              {(milestone.milestoneCode === 'STORE_TASK_GENERATION' || milestone.milestoneCode === 'DISPLAY_DIAGRAM_GENERATION') ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Text className="assignment-label">{t('planManagement.assignment.systemGenerated')}</Text>
                  <Tag color="blue">{t('planManagement.assignment.automated')}</Tag>
                </div>
              ) : (
                <>
                  <Button
                    type="link"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => handleAddSubtask(milestone.milestoneId)}
                    className="add-subtask-button"
                  >
                  {t('planManagement.assignment.actions.addSubtask')}
                  </Button>
                </>
              )}
            </div>
          </div>
        ),
        children: assignment?.subtasks?.map((subtask, index) => {
          const subtaskKey = `${milestone.milestoneId}-${index}`
          const assigneeName = availablePersonnel.find(p => p.id === subtask.assignee)?.name || subtask.assignee
          const isCancelled = subtask.status === 'Cancelled'
          const isFinished = subtask.status === 'Finished'
          const isOngoing = subtask.status === 'Ongoing'
          
          return {
            key: subtaskKey,
            title: (
              <div className={`subtask-tree-node ${isCancelled ? 'cancelled' : ''}`}>
                <div className="subtask-content">

                  <FileTextOutlined style={{ color: isCancelled ? '#ccc' : '#3b82f6' }} />
                  <Text className={`subtask-name ${isCancelled ? 'cancelled' : ''}`}>
                    {subtask.taskName}
                  </Text>
                  {isCancelled && <Tag className="cancelled-tag">{t('planManagement.assignment.cancelled')}</Tag>}
                  <Text className="subtask-assignee">
                    {t('planManagement.assignment.assigneeName', { name: assigneeName })}
                  </Text>
                  <Text className="subtask-time">
                    {t('planManagement.assignment.completionTime', { time: dayjs(subtask.plannedCompletionTime).format('YYYY-MM-DD HH:mm') })}
                  </Text>
                  {subtask.requiresFiles && (
                    <Tag className={`requires-files-tag ${isCancelled ? 'cancelled' : ''}`}>
                      {t('planManagement.assignment.requiresFiles')}
                    </Tag>
                  )}
                  {/* Edit button - cancelled subtasks don't show edit button */}
                  {!isCancelled && !isFinished && !isOngoing && (
                    <Button
                      type="link"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => handleEditSubtask(milestone.milestoneId, index)}
                      className="subtask-action-button edit-button"
                    >
                      {t('planManagement.assignment.edit')}
                    </Button>
                  )}
                  {!isCancelled && !isFinished && !isOngoing && (
                    <Button
                      type="link"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteSubtask(subtask.id, milestone.milestoneId, index)}
                      className="subtask-action-button delete-button"
                    >
                      {t('planManagement.assignment.delete')}
                    </Button>
                  )}
                  {!isCancelled && !isFinished && !isOngoing && (
                    <>
                      {subtask.isNewlyAdded && (
                        <Button
                          type="link"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteSubtask(subtask.id, milestone.milestoneId, index)}
                          className="subtask-action-button delete-button"
                        >
                          {t('planManagement.assignment.delete')}
                        </Button>
                      )}
                      {!subtask.isNewlyAdded && !isCancelled && (
                        <Button
                          type="link"
                          size="small"
                          icon={<CloseOutlined />}
                          onClick={() => handleCancelSubtask(subtask.id, milestone.milestoneId, index)}
                          className="subtask-action-button cancel-button"
                        >
                          {t('planManagement.assignment.cancel')}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            ),
          }
        }) || [],
      }
    })
  }

  // Handle add subtask
  const handleAddSubtask = async (milestoneId: string) => {
    // 根据里程碑的负责部门查询操作组数据
    try {
      const milestone = milestones.find(m => m.milestoneId === milestoneId)
      if (milestone) {
        // 使用操作组服务查询数据，传入divisionName参数
        const response = await operatorGroupService.listOperatorGroups({ responsibleTeam: milestone.responsibleDepartment, status: 'ENABLE' })
        if (response.success && response.data) {
          // 提取所有director作为负责人下拉数据
          const directorSet = new Set()
          const directorData = response.data.tableData
            .filter(group => group.director) // 确保director存在
            .map(group => {
               return {
                  value: group.code,
                  label: group.director
                }
            })
            .filter(Boolean) as any[]
          
          setDirectors(directorData)
          // 初始时不设置availablePersonnel，保持为空数组
          setAvailablePersonnel([])
        } else {
          setDirectors([])
          setAvailablePersonnel([])
        }
      }
    } catch (error) {
      console.error('Failed to load operator group data:', error)
      // Fallback to mock data
      setDirectors([])
      setAvailablePersonnel([])
    }
    
    setSelectedMilestone(milestoneId)
    setEditingSubtask(null)
    setSelectedDirector('') // 重置选中的负责人
    form.resetFields()
    setSubtaskModalVisible(true)
  }

  // Handle edit subtask
  const handleEditSubtask = async (milestoneId: string, index: number) => {
    const assignment = assignmentData?.assignments.find(a => a.milestoneId === milestoneId)
    const subtask = assignment?.subtasks?.[index]
    
    if (subtask) {
      // Check if subtask is cancelled
      const isCancelled = subtask.description?.startsWith(t('planManagement.assignment.cancelledPrefix'))
      if (isCancelled) {
        message.warning(t('planManagement.assignment.messages.cancelledTaskCannotEdit'))
        return
      }
      
      // 根据里程碑的负责部门查询操作组数据
      try {
        const milestone = milestones.find(m => m.milestoneId === milestoneId)
        if (milestone) {
          // 使用操作组服务查询数据，传入divisionName参数
          const response = await operatorGroupService.listOperatorGroups({ responsibleTeam: milestone.responsibleDepartment })
          if (response.success && response.data) {
            // 提取所有director作为负责人下拉数据
            const directorSet = new Set()
            const directorData = response.data.tableData
              .filter(group => group.director) // 确保director存在
              .map(group => {
                return {
                  value: group.code,
                  label: group.director
                }
              })
              .filter(Boolean) as any[]
            
            setDirectors(directorData)
            // 初始时不设置availablePersonnel，保持为空数组
            setAvailablePersonnel([])
          } else {
            setDirectors([])
            setAvailablePersonnel([])
          }
        }
      } catch (error) {
        console.error('Failed to load operator group data:', error)
        // Fallback to mock data
        setDirectors([])
        setAvailablePersonnel([])
      }
      
      // Cancelled subtasks have been checked above, here we allow all non-cancelled subtasks to be edited
      
      setSelectedMilestone(milestoneId)
      setEditingSubtask({ milestoneId, index })
      setSelectedDirector('') // 重置选中的负责人
      form.setFieldsValue({
        taskName: subtask.taskName,
        assignee: subtask.assignee,
        plannedCompletionTime: dayjs(subtask.plannedCompletionTime),
        requiresFiles: subtask.requiresFiles,
        description: subtask.description
      })
      setSubtaskModalVisible(true)
    }
  }

  // Handle delete subtask
  const handleDeleteSubtask = (id, milestoneId: string, index: number) => {
    Modal.confirm({
      title: t('planManagement.assignment.confirmDelete'),
      content: t('planManagement.assignment.confirmDeleteContent'),
      onOk: async () => {

        const response = await PlanogramSubtaskService.deleteSubtask(id)
        
        if (response.success) {
          dispatch(removeSubtask({ milestoneId, subtaskIndex: index }))
          message.success(t('planManagement.assignment.deleteSuccess'))
        } else {
          message.error(response.errorMessage || t('planManagement.assignment.messages.saveFailed'))
          return
        }
      }
    })
  }

  // Handle cancel subtask (for in-progress plans)
  const handleCancelSubtask = (id, milestoneId: string, index: number) => {
    Modal.confirm({
      title: t('planManagement.assignment.confirmCancel'),
      content: t('planManagement.assignment.confirmCancelContent'),
      onOk: async () => {
        const response = await PlanogramSubtaskService.cancelSubtask(id)
        if (response.success) {
          dispatch(updateSubtask({
            milestoneId,
            subtaskIndex: index,
            subtask: {
              status: 'Cancelled',
            }
          }))
          message.success(t('planManagement.assignment.cancelSuccess'))
        }
      }
    })
  }

  // Recommend optimal date for task completion
  const recommendOptimalDate = () => {
    const milestone = milestones.find(m => m.milestoneId === selectedMilestone)
    if (!milestone) {
      message.warning(t('planManagement.assignment.messages.selectMilestoneFirst'))
      return
    }

    const milestoneStart = dayjs(milestone.plannedStartTime)
    
    // Simply recommend milestone start time (removed conflict detection)
    const recommendedDate = milestoneStart.hour(16).minute(0).second(0)
    message.success(t('planManagement.assignment.messages.recommendedStartTime'))

    // Set the recommended date to the form
    form.setFieldsValue({
      plannedCompletionTime: recommendedDate
    })
  }

  // Handle subtask form submission
  const handleSubtaskSubmit = async () => {
    try {
      const values = await form.validateFields()
      
      // 获取当前里程碑信息
      const currentMilestone = milestones.find(m => m.milestoneId === selectedMilestone)
      
      // 准备子任务数据
      const subtaskData = {
        planDetailId: currentMilestone.id,
        taskName: values.taskName,
        assignee: values.assignee,
        plannedCompletionTime: values.plannedCompletionTime.valueOf(),
        requiresFiles: values.requiresFiles,
        description: values.description,
        milestoneName: currentMilestone?.milestoneName,
        planCode: currentPlan?.planCode,
        planName: currentPlan?.planName
      }

      if (editingSubtask) {
        // 更新现有子任务
        const assignment = assignmentData?.assignments.find(a => a.milestoneId === editingSubtask.milestoneId)
        if (assignment && assignment.subtasks) {
          const existingSubtask = assignment.subtasks[editingSubtask.index]
          
          // 准备更新数据
          const updateData = {
            id: (existingSubtask as any).id, // 如果已有ID则使用，否则会在后端生成
            ...subtaskData,
            // 保留现有字段
            isNewlyAdded: existingSubtask.isNewlyAdded,
            createdAt: existingSubtask.createdAt
          }
          
          // 调用后端API更新子任务
          const response = await PlanogramSubtaskService.updateSubtask(updateData)
          
          if (response.success) {
            // 更新Redux状态
            const subtaskData: SubtaskConfiguration = {
              id: response.data,
              taskName: values.taskName,
              assignee: values.assignee,
              plannedCompletionTime: values.plannedCompletionTime.valueOf(),
              requiresFiles: values.requiresFiles,
              description: values.description,
              // Preserve existing isNewlyAdded and createdAt fields
              isNewlyAdded: existingSubtask.isNewlyAdded,
              createdAt: existingSubtask.createdAt
            }
            dispatch(updateSubtask({
              milestoneId: editingSubtask.milestoneId,
              subtaskIndex: editingSubtask.index,
              subtask: subtaskData
            }))
            message.success(t('planManagement.assignment.messages.subtaskUpdated'))
          } else {
            message.error(response.errorMessage || t('planManagement.assignment.messages.saveFailed'))
            return
          }
        }
      } else {
        // 创建新子任务
        const response = await PlanogramSubtaskService.createSubtask(subtaskData)
        
        if (response.success) {
          // 添加到Redux状态
          const subtaskData: SubtaskConfiguration = {
            id: response.data,
            taskName: values.taskName,
            assignee: values.assignee,
            plannedCompletionTime: values.plannedCompletionTime.valueOf(),
            requiresFiles: values.requiresFiles,
            description: values.description,
            // New subtask: if plan is in progress, mark as newly added
            isNewlyAdded: currentPlan?.status === 'InProgress',
            createdAt: Date.now()
          }
          dispatch(addSubtask({ milestoneId: selectedMilestone, subtask: subtaskData }))
          message.success(t('planManagement.assignment.messages.subtaskAdded'))
        } else {
          message.error(response.errorMessage || t('planManagement.assignment.messages.saveFailed'))
          return
        }
      }

      setSubtaskModalVisible(false)
      form.resetFields()
    } catch (error: any) {
      message.error(error.message || t('planManagement.assignment.messages.saveFailed'))
    }
  }

  // Handle final submission
  const handleSubmit = async () => {
    if (!assignmentData) return
    
    // Validate first
    // await dispatch(validateAssignmentData(assignmentData) as any)
    
    // if (validationErrors.length > 0) {
    //   message.error(t('planManagement.assignment.messages.configValidationFailed'))
    //   return
    // }

    Modal.confirm({
      title: t('planManagement.assignment.confirmSubmit.title'),
      content: (
        <div>
          <p>{t('planManagement.assignment.confirmSubmit.description')}</p>
          <ul style={{ marginTop: '12px', paddingLeft: '20px' }}>
            <li>{t('planManagement.assignment.confirmSubmit.completedInfo', { completed: progress.completed, total: progress.total })}</li>
            <li>{t('planManagement.assignment.confirmSubmit.validationInfo')}</li>
            <li>{t('planManagement.assignment.confirmSubmit.confirmationInfo')}</li>
          </ul>
          <p style={{ marginTop: '12px', color: '#ff4d4f' }}>{t('planManagement.assignment.confirmSubmit.confirmQuestion')}</p>
        </div>
      ),
      width: 480,
      okText: t('planManagement.assignment.confirmSubmit.ok'),
      cancelText: t('planManagement.assignment.confirmSubmit.cancel'),
      onOk: async () => {
        try {
          // Validate configuration first
          // const validation = await dispatch(validateAssignmentData(assignmentData) as any)
          
          // if (!validation.payload.isValid) {
          //   message.error(t('planManagement.assignment.messages.configValidationFailedFix'))
          //   return // Stay on current page
          // }
          
          // 保存所有子任务到后端
          // let hasError = false
          // for (const assignment of assignmentData.assignments) {
          //   for (let i = 0; i < (assignment.subtasks?.length || 0); i++) {
          //     const subtask = assignment.subtasks![i]
          //     const milestone = milestones.find(m => m.milestoneId === assignment.milestoneId)
              
          //     // 准备子任务数据
          //     const subtaskData = {
          //       taskName: subtask.taskName,
          //       assignee: subtask.assignee,
          //       plannedCompletionTime: subtask.plannedCompletionTime,
          //       requiresFiles: subtask.requiresFiles,
          //       description: subtask.description,
          //       milestoneName: milestone?.milestoneName,
          //       planCode: currentPlan?.planCode,
          //       planName: currentPlan?.planName
          //     }
              
          //     // 如果子任务有ID，则更新，否则创建
          //     let response
          //     if ((subtask as any).id) {
          //       response = await PlanogramSubtaskService.updateSubtask({
          //         id: (subtask as any).id,
          //         ...subtaskData
          //       })
          //     } else {
          //       response = await PlanogramSubtaskService.createSubtask(subtaskData)
          //     }
              
          //     if (!response.success) {
          //       hasError = true
          //       message.error(`${t('planManagement.assignment.messages.subtaskSaveFailed')}: ${response.errorMessage}`)
          //     }
          //   }
          // }
          
          // if (hasError) {
          //   message.error(t('planManagement.assignment.messages.submitFailed'))
          //   return
          // }
          
          // 如果验证通过且所有子任务保存成功，则提交并导航
          message.success(t('planManagement.assignment.messages.assignmentSuccess'))
          navigate(`/detail/${id}`)
        } catch (error) {
          message.error(error.message || t('planManagement.assignment.messages.submitFailed'))
          // Stay on current page on error
        }
      }
    })
  }

  const progress = calculateAssignmentProgress()
  const loading = planLoading || milestonesLoading || saving

  if (loading && !assignmentData) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>
          <Text>{t('planManagement.assignment.status.loadingConfig')}</Text>
        </div>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
      {/* Page Header */}
      <div style={{ 
        backgroundColor: '#fafafa', 
        borderBottom: '1px solid #e5e7eb',
        padding: '24px 32px'
      }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={handleBack}
          style={{ 
            padding: 0, 
            height: 'auto',
            color: '#6b7280',
            fontSize: '14px'
          }}
        >
          {t('planManagement.assignment.header.back')}
        </Button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
          <div>
            <Title level={2} style={{ margin: 0, color: '#1f2937' }}>
              <TeamOutlined style={{ marginRight: '8px' }} /> {t('planManagement.assignment.header.title')}
            </Title>
            <Text type="secondary" style={{ fontSize: '14px', marginTop: '4px' }}>
              {t('planManagement.assignment.header.planInfo')}: {currentPlan?.planName} ({currentPlan?.planCode})
            </Text>
          </div>
          <Space>
            {/* <Button
              icon={<SaveOutlined />}
              onClick={handleSaveDraft}
              // disabled={!isDirty}
              loading={saving}
              style={{
                borderRadius: '8px',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
              }}
            >
              {t('planManagement.assignment.header.saveDraft')}
            </Button> */}
            {/* <Button
              icon={<CheckOutlined />}
              onClick={handleValidate}
              disabled={!assignmentData}
              style={{
                borderRadius: '8px',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
              }}
            >
              {t('planManagement.assignment.header.validateConfig')}
            </Button> */}
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={handleSubmit}
              disabled={!assignmentData || validationErrors.length > 0}
              loading={saving}
              style={{
                borderRadius: '8px',
                backgroundColor: '#3b82f6',
                borderColor: '#3b82f6',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
              }}
            >
              {t('planManagement.assignment.header.submitConfig')}
            </Button>
          </Space>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ padding: '24px' }}>

        {/* Progress */}
        <Card className="progress-card">
          <div className="progress-content">
            <Text className="progress-label">{t('planManagement.assignment.progress.label')}</Text>
            <Progress
              percent={progress.percentage}
              status={progress.percentage === 100 ? 'success' : 'active'}
              className="progress-bar"
              strokeColor={{
                '0%': '#3b82f6',
                '100%': '#10b981',
              }}
            />
            <Text className="progress-stats">
              {t('planManagement.assignment.progress.stats', { completed: progress.completed, total: progress.total })}
            </Text>
          </div>
          {currentPlan?.status === 'InProgress' && (
            <Alert
              type="info"
              message={t('planManagement.assignment.progress.planInProgressTitle')}
              description={t('planManagement.assignment.progress.planInProgressDesc')}
              showIcon
              className="status-alert"
            />
          )}
        </Card>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <Alert
            type="error"
            message={t('planManagement.assignment.alerts.validationFailedTitle')}
            description={
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {validationErrors.map((error, index) => {
                  const localizedError = localizeValidationError(error, t)
                  return <li key={index}>{localizedError.message}</li>
                })}
              </ul>
            }
            className="validation-alert"
            closable
            onClose={() => dispatch(clearValidationErrors())}
          />
        )}

        {/* Error Alert */}
        {error && (
          <Alert
            type="error"
            message={t('planManagement.assignment.alerts.operationFailedTitle')}
            description={error}
            className="validation-alert"
            closable
          />
        )}



        {/* Milestone Tree */}
        <Card 
          title={t('planManagement.assignment.tree.milestoneConfigTitle')} 
          extra={
            <div className="tree-controls">
              <Button
                size="small"
                onClick={() => setExpandedKeys(milestones.map(m => m.milestoneId))}
                className="tree-control-button"
              >
                {t('planManagement.assignment.tree.expandAll')}
              </Button>
              <Button
                size="small"
                onClick={() => setExpandedKeys([])}
                className="tree-control-button"
              >
                {t('planManagement.assignment.tree.collapseAll')}
              </Button>

            </div>
          }
          className="milestone-tree-card fade-in-up"
        >
          {milestones?.length > 0 ? (
            <Tree
              treeData={generateTreeData()}
              expandedKeys={expandedKeys}
              onExpand={setExpandedKeys}
              showLine
              showIcon={false}
            />
          ) : (
            <div className="empty-state">
              <Text type="secondary">{t('planManagement.assignment.tree.noMilestoneData')}</Text>
            </div>
          )}
        </Card>
      </div>

      {/* Subtask Modal */}
      <Modal
        title={editingSubtask ? t('planManagement.assignment.modal.editSubtask') : t('planManagement.assignment.modal.addSubtask')}
        open={subtaskModalVisible}
        onOk={handleSubtaskSubmit}
        onCancel={() => {
          setSubtaskModalVisible(false)
          form.resetFields()
        }}
        width={700}
        destroyOnClose
        className="subtask-modal"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            requiresFiles: false,
            milestoneOwner: undefined
          }}
        >
          {/* Milestone Context */}
          {selectedMilestone && (
            <Alert
              type="info"
              message={t('planManagement.assignment.modal.currentMilestone', { name: milestones.find(m => m.milestoneId === selectedMilestone)?.milestoneName })}
              description={
                <div>
                  <Text type="secondary">
                    {t('planManagement.assignment.modal.milestoneTimeRange', {
                      start: dayjs(milestones.find(m => m.milestoneId === selectedMilestone)?.plannedStartTime).format('YYYY-MM-DD'),
                      end: dayjs(milestones.find(m => m.milestoneId === selectedMilestone)?.plannedEndTime).format('YYYY-MM-DD')
                    })}
                  </Text>
                </div>
              }
              style={{ marginBottom: '16px' }}
            />
          )}

          <Form.Item
            name="taskName"
            label={t('planManagement.assignment.modal.taskName')}
            rules={[
              { required: true, message: t('planManagement.assignment.modal.taskNameRequired') },
              { min: 2, message: t('planManagement.assignment.modal.taskNameMinLength') },
              { max: 50, message: t('planManagement.assignment.modal.taskNameMaxLength') }
            ]}
          >
            <Input placeholder={t('planManagement.assignment.modal.taskNamePlaceholder')} showCount maxLength={50} />
          </Form.Item>

          {/* Milestone Owner Selection - Moved from tree view */}
          <Form.Item
            name="milestoneOwner"
            label={t('planManagement.assignment.selectAssignee')}
            rules={[
              { required: true }
            ]}
          >
            <Select
              style={{ width: '100%' }}
              placeholder={t('planManagement.assignment.selectAssignee')}
              size="middle"
              className="milestone-owner-select"
              allowClear
              showSearch
              filterOption={(input, option) =>
                option?.label?.toString().toLowerCase().includes(input.toLowerCase()) || false
              }
              optionLabelProp="label"
              onChange={async (value) => {
                // 当选择负责人时，获取该负责人团队的工作人员
                setSelectedDirector(value as string)
                if (value) {
                  // 通过director查找对应的操作组，获取workers
                  try {
                    const detailResponse = await operatorGroupService.getOperatorGroupByCode(value)
                    if (detailResponse.success && detailResponse.data) {
                      // 设置该操作组的工作人员为可用人员
                      const personnelData = detailResponse.data.workers.map(worker => ({
                        id: worker.name + '-' + worker.accountId,
                        name: worker.name
                      }))
                      setAvailablePersonnel(personnelData)
                    } else {
                      setAvailablePersonnel([])
                    }
                  } catch (error) {
                    console.error('Failed to load operator group data:', error)
                    setAvailablePersonnel([])
                  }
                } else {
                  // 如果没有选择负责人，清空可用人员列表
                  setAvailablePersonnel([])
                }
              }}
            >
              {directors.map(person => {
                return (
                  <Option 
                    key={person.value} 
                    value={person.value}
                    label={`${person.label}`}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div>{person.label}</div>
                      </div>
                    </div>
                  </Option>
                )
              })}
            </Select>
          </Form.Item>

          <Form.Item
            name="assignee"
            label={t('planManagement.assignment.modal.assignee')}
            rules={[
              { required: true, message: t('planManagement.assignment.modal.assigneeRequired') },
            ]}
          >
            <Select 
              placeholder={t('planManagement.assignment.modal.assigneePlaceholder')}
              showSearch
              filterOption={(input, option) =>
                option?.label?.toString().toLowerCase().includes(input.toLowerCase()) || false
              }
              optionLabelProp="label"
            >
              {availablePersonnel?.map(person => {
                  const currentWorkload = getPersonnelWorkload(person.id)
                  
                  return (
                    <Option 
                      key={person.id} 
                      value={person.id}
                      label={`${person.name}`}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div>{person.name}</div>
                        </div>
                        <div style={{ 
                          fontSize: '12px', 
                          color: '#666',
                          marginLeft: '8px'
                        }}>
                          {t('planManagement.assignment.modal.assignedTasks')}: {currentWorkload}
                        </div>
                      </div>
                    </Option>
                  )
                })}
            </Select>
          </Form.Item>

          <Form.Item
            name="plannedCompletionTime"
            label={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>{t('planManagement.assignment.modal.plannedCompletionTime')}</span>
                <Tooltip 
                  title={t('planManagement.assignment.modal.recommendDateTooltip')}
                  placement="top"
                >
                  <Button 
                    type="link"
                    size="small"
                    icon={<BulbOutlined />}
                    onClick={recommendOptimalDate}
                    style={{ padding: '0 4px', height: 'auto' }}
                  >
                    {t('planManagement.assignment.modal.recommendDate')}
                  </Button>
                </Tooltip>
              </div>
            }
            rules={[
              { required: true, message: t('planManagement.assignment.modal.plannedTimeRequired') },
              // { 
              //   validator: (_, value) => {
              //     const assigneeId = form.getFieldValue('assignee')
              //     return validateSubtaskTimeWithConflicts(value, assigneeId)
              //   }
              // }
            ]}
          >
            <DatePicker 
              style={{ width: '100%' }} 
              // showTime={{ format: 'HH:mm' }}
              format="YYYY-MM-DD"
              disabledDate={(current) => {
                const milestone = milestones.find(m => m.milestoneId === selectedMilestone)
                if (!milestone) return false
                
                const milestoneStart = dayjs(milestone.plannedStartTime).startOf('day')
                const milestoneEnd = dayjs(milestone.plannedEndTime).endOf('day')
                
                return current && (current.isBefore(milestoneStart) || current.isAfter(milestoneEnd))
              }}
            />
          </Form.Item>

          <Form.Item
            name="requiresFiles"
            label={t('planManagement.assignment.modal.fileRequirement')}
            valuePropName="checked"
          >
            <Switch 
              checkedChildren={t('planManagement.assignment.modal.requiresFiles')} 
              unCheckedChildren={t('planManagement.assignment.modal.noFilesRequired')}
              onChange={(checked) => {
                if (checked) {
                  message.info(t('planManagement.assignment.modal.fileUploadReminder'))
                }
              }}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label={t('planManagement.assignment.modal.taskDescription')}
            rules={[
              { max: 200, message: t('planManagement.assignment.modal.descriptionMaxLength') }
            ]}
          >
            <TextArea 
              rows={3} 
              placeholder={t('planManagement.assignment.modal.descriptionPlaceholder')} 
              showCount 
              maxLength={200}
            />
          </Form.Item>

          {/* Validation Tips */}
          <Alert
            type="warning"
            message={t('planManagement.assignment.modal.importantNotes')}
            description={
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li>{t('planManagement.assignment.modal.note1')}</li>
                <li>{t('planManagement.assignment.modal.note2')}</li>
                <li>{t('planManagement.assignment.modal.note3')}</li>
              </ul>
            }
            style={{ marginTop: '16px' }}
          />
        </Form>
      </Modal>


    </div>
  )
}

export default PersonnelAssignment